/**
 * Small shared admin UI atoms.
 *
 * Kept in one file so the individual view components stay short and
 * consistent. Nothing here is generic enough to belong in `components/ui/`.
 */

const STATUS_STYLES = {
  pending:         "bg-tangerine/15 text-tangerine ring-tangerine/30",
  "in-review":     "bg-cobalt/15    text-cobalt    ring-cobalt/30",
  "info-requested":"bg-blossom/25   text-ink       ring-blossom/40",
  approved:        "bg-grass/15     text-grass     ring-grass/30",
  rejected:        "bg-scarlet/15   text-scarlet   ring-scarlet/30",
  verified:        "bg-grass/15     text-grass     ring-grass/30",
  active:          "bg-grass/15     text-grass     ring-grass/30",
  live:            "bg-cobalt/15    text-cobalt    ring-cobalt/30",
  draft:           "bg-ink/10       text-ink       ring-ink/20",
  closed:          "bg-ink/10       text-ink       ring-ink/20",
  flagged:         "bg-scarlet/15   text-scarlet   ring-scarlet/30",
  ready:           "bg-yellow/40    text-ink       ring-yellow/70",
  processing:      "bg-cobalt/15    text-cobalt    ring-cobalt/30",
  sent:            "bg-grass/15     text-grass     ring-grass/30",
};

export const StatusBadge = ({ status, className = "" }) => {
  const cls = STATUS_STYLES[status] || "bg-ink/10 text-ink ring-ink/20";
  const label = status.replace(/-/g, " ");
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset ${cls} ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {label}
    </span>
  );
};

export const SectionHeader = ({ eyebrow, title, description, action, className = "" }) => (
  <div className={`flex items-end justify-between gap-6 ${className}`}>
    <div>
      {eyebrow && (
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
          {eyebrow}
        </p>
      )}
      <h2 className="mt-2 font-serif text-3xl leading-tight text-ink lg:text-4xl">
        {title}
      </h2>
      {description && (
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/70">
          {description}
        </p>
      )}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

export const StatCard = ({ label, value, accent = "ink", helper, testid }) => {
  const accentTextCls = {
    ink: "text-ink",
    tangerine: "text-tangerine",
    cobalt: "text-cobalt",
    grass: "text-grass",
    blossom: "text-blossom",
    yellow: "text-ink",
  }[accent] || "text-ink";
  return (
    <div
      data-testid={testid}
      className="flex h-full flex-col rounded-lg border border-ink/10 bg-sand p-6"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">{label}</p>
      <p className={`mt-3 font-serif text-4xl leading-none tracking-tight ${accentTextCls}`}>
        {value}
      </p>
      {helper && <p className="mt-3 text-xs leading-relaxed text-ink/60">{helper}</p>}
    </div>
  );
};

export const AdminButton = ({ children, onClick, variant = "primary", size = "md", testid, disabled }) => {
  const base = "inline-flex items-center gap-2 rounded-full font-medium tracking-tight transition disabled:opacity-40 disabled:cursor-not-allowed";
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
  };
  const variants = {
    primary: "bg-ink text-sand hover:bg-ink/90",
    secondary: "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40",
    approve: "bg-grass text-sand hover:brightness-95",
    reject: "bg-scarlet text-sand hover:brightness-95",
    info: "bg-tangerine text-sand hover:brightness-95",
    ghost: "text-ink hover:bg-ink/5",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${sizes[size]} ${variants[variant]}`}
    >
      {children}
    </button>
  );
};

export const AdminCheckbox = ({ checked, onChange, label, testid, hint }) => (
  <label className="flex cursor-pointer items-start gap-3" data-testid={testid}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink/30 text-ink focus:ring-ink/30"
    />
    <div>
      <span className="text-sm text-ink">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-ink/50">{hint}</span>}
    </div>
  </label>
);

export const EmptyState = ({ title, description }) => (
  <div className="rounded-lg border border-dashed border-ink/15 bg-sand/40 p-10 text-center">
    <p className="font-serif text-xl text-ink">{title}</p>
    {description && <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">{description}</p>}
  </div>
);

export const KpiLine = ({ label, value }) => (
  <div className="flex items-baseline justify-between border-b border-ink/10 py-3 last:border-0">
    <span className="text-sm text-ink/70">{label}</span>
    <span className="font-serif text-lg text-ink">{value}</span>
  </div>
);
